import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Swords } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  team_a_id: "",
  team_b_id: "",
  league_a_id: "",
  league_b_id: "",
  date: "",
  home_goals: "",
  away_goals: "",
};

function teamLabel(t) {
  return t ? `${t.name} (${t.country})` : "—";
}

export default function MatchesPage() {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, t, l] = await Promise.all([
        api.get("/matches"), api.get("/teams"), api.get("/leagues"),
      ]);
      setMatches(m.data); setTeams(t.data); setLeagues(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const teamsById = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);

  const canCreate = teams.length >= 2 && leagues.length >= 1;

  const openCreate = () => { setCreateForm(emptyForm); setCreateOpen(true); };

  const openEdit = (m) => {
    setEditing(m);
    setEditForm({
      team_a_id: m.team_a_id,
      team_b_id: m.team_b_id,
      league_a_id: m.league_a_id,
      league_b_id: m.league_b_id,
      date: m.date,
      home_goals: m.home_goals == null ? "" : String(m.home_goals),
      away_goals: m.away_goals == null ? "" : String(m.away_goals),
    });
  };

  const payloadFrom = (f) => {
    const p = {
      team_a_id: f.team_a_id,
      team_b_id: f.team_b_id,
      league_a_id: f.league_a_id,
      league_b_id: f.league_b_id || f.league_a_id,
      date: f.date,
      home_goals: f.home_goals === "" ? null : parseInt(f.home_goals, 10),
      away_goals: f.away_goals === "" ? null : parseInt(f.away_goals, 10),
    };
    return p;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (createForm.team_a_id === createForm.team_b_id) {
      toast.error("Pick two different teams"); return;
    }
    setSaving(true);
    try {
      await api.post("/matches", payloadFrom(createForm));
      toast.success("Match created"); setCreateOpen(false); await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (editForm.team_a_id === editForm.team_b_id) {
      toast.error("Pick two different teams"); return;
    }
    setSaving(true);
    try {
      await api.patch(`/matches/${editing.id}`, payloadFrom(editForm));
      toast.success("Match updated"); setEditing(null); await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/matches/${toDelete.id}`);
      toast.success("Match deleted"); setToDelete(null); await load();
    } catch (err) { toast.error(formatApiError(err)); setToDelete(null); }
  };

  const renderForm = (form, setForm, prefix, onSubmit, submitLabel) => {
    const teamAOptions = teams.filter((t) => t.id !== form.team_b_id);
    const teamBOptions = teams.filter((t) => t.id !== form.team_a_id);
    const teamALeagues = teamsById[form.team_a_id]?.league_ids || [];
    const teamBLeagues = teamsById[form.team_b_id]?.league_ids || [];
    const leaguesFor = (ids) => leagues.filter((l) => ids.includes(l.id));
    const setLeagueA = (v) => setForm({ ...form, league_a_id: v, league_b_id: form.league_b_id || v });
    return (
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Team A</Label>
            <Select value={form.team_a_id} onValueChange={(v) => setForm({ ...form, team_a_id: v, league_a_id: "" })}>
              <SelectTrigger data-testid={`${prefix}-team-a-select`}><SelectValue placeholder="Select team" /></SelectTrigger>
              <SelectContent>
                {teamAOptions.map((t) => <SelectItem key={t.id} value={t.id}>{teamLabel(t)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Team B</Label>
            <Select value={form.team_b_id} onValueChange={(v) => setForm({ ...form, team_b_id: v, league_b_id: "" })}>
              <SelectTrigger data-testid={`${prefix}-team-b-select`}><SelectValue placeholder="Select team" /></SelectTrigger>
              <SelectContent>
                {teamBOptions.map((t) => <SelectItem key={t.id} value={t.id}>{teamLabel(t)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>League (Team A)</Label>
            <Select value={form.league_a_id} onValueChange={setLeagueA} disabled={!form.team_a_id}>
              <SelectTrigger data-testid={`${prefix}-league-a-select`}><SelectValue placeholder={form.team_a_id ? "Select league" : "Pick Team A first"} /></SelectTrigger>
              <SelectContent>
                {leaguesFor(teamALeagues).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>League (Team B)</Label>
            <Select value={form.league_b_id} onValueChange={(v) => setForm({ ...form, league_b_id: v })} disabled={!form.team_b_id}>
              <SelectTrigger data-testid={`${prefix}-league-b-select`}><SelectValue placeholder={form.team_b_id ? "Select league" : "Pick Team B first"} /></SelectTrigger>
              <SelectContent>
                {leaguesFor(teamBLeagues).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2 col-span-3 sm:col-span-1">
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid={`${prefix}-date-input`} />
          </div>
          <div className="space-y-2">
            <Label>Home goals</Label>
            <Input type="number" min="0" value={form.home_goals} onChange={(e) => setForm({ ...form, home_goals: e.target.value })} placeholder="—" data-testid={`${prefix}-home-goals-input`} />
          </div>
          <div className="space-y-2">
            <Label>Away goals</Label>
            <Input type="number" min="0" value={form.away_goals} onChange={(e) => setForm({ ...form, away_goals: e.target.value })} placeholder="—" data-testid={`${prefix}-away-goals-input`} />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); setEditing(null); }}>Cancel</Button>
          <Button
            type="submit"
            disabled={saving || !form.team_a_id || !form.team_b_id || !form.league_a_id || !form.league_b_id || !form.date}
            data-testid={`${prefix}-submit`}
          >
            {saving ? "Saving…" : submitLabel}
          </Button>
        </DialogFooter>
      </form>
    );
  };

  return (
    <div data-testid="matches-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Fixtures</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight">Matches</h1>
          <p className="text-muted-foreground mt-2 max-w-lg">Fixtures across leagues, including cross-competition cup ties.</p>
        </div>
        <Button onClick={openCreate} disabled={!canCreate} className="h-11 rounded-none font-display uppercase tracking-[0.18em]" data-testid="create-match-button">
          <Plus className="h-4 w-4 mr-2" /> New Match
        </Button>
      </header>

      {!canCreate && !loading && (
        <div className="rounded-xl border border-border bg-card p-6 mb-6 text-sm text-muted-foreground">
          You need at least 2 teams and 1 league before creating matches.
        </div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <Table data-testid="matches-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Fixture</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">League</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Date</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Score</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="px-6 py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : matches.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="px-6 py-16 text-center">
                <Swords className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <div className="font-display uppercase tracking-wider">No matches yet</div>
              </TableCell></TableRow>
            ) : (
              matches.map((m) => {
                const sameLeague = m.league_a_id === m.league_b_id;
                return (
                  <TableRow key={m.id} className="tr-fade border-border hover:bg-muted/30" data-testid={`match-row-${m.id}`}>
                    <TableCell className="px-6 py-4">
                      <div className="font-medium">
                        {m.team_a?.name || "Team A"} <span className="text-muted-foreground">vs</span> {m.team_b?.name || "Team B"}
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm">
                      {sameLeague
                        ? (m.league_a?.name || "—")
                        : <span>{m.league_a?.name || "—"} <span className="text-muted-foreground">/</span> {m.league_b?.name || "—"}</span>}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-muted-foreground">{m.date}</TableCell>
                    <TableCell className="px-6 py-4 font-mono">
                      {m.home_goals == null && m.away_goals == null
                        ? <span className="text-muted-foreground">—</span>
                        : `${m.home_goals ?? "?"} – ${m.away_goals ?? "?"}`}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-match-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setToDelete(m)} data-testid={`delete-match-${m.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="create-match-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">New Match</DialogTitle>
            <DialogDescription>Pick two teams; each has its own league for the fixture.</DialogDescription>
          </DialogHeader>
          {renderForm(createForm, setCreateForm, "match-create", handleCreate, "Create match")}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-match-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Edit Match</DialogTitle>
          </DialogHeader>
          {editing && renderForm(editForm, setEditForm, "match-edit", handleEdit, "Save changes")}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete match?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete the fixture on {toDelete?.date}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-match-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
